import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { DollarSign, Copy, X, FileDown, Plus, User, MapPin, CalendarDays, DollarSign as MoneyIcon, Trash2, Pencil, FileSignature, Send, RotateCcw, Link2, Truck } from "lucide-react";
import { api } from "../../lib/api";
import { Skeleton, F, useConfirm } from "./_shared";
import FinalDeliveryModal from "./FinalDeliveryModal";

const CONTRACT_STATUS_STYLE = {
  sent:      { label: "Sent",      cls: "border-cyan/40 text-cyan bg-cyan/10" },
  viewed:    { label: "Viewed",    cls: "border-indigo-400/40 text-indigo-300 bg-indigo-400/10" },
  signed:    { label: "Signed",    cls: "border-lime-400/40 text-lime-300 bg-lime-400/10" },
  completed: { label: "Completed", cls: "border-lime-400/50 text-lime-200 bg-lime-400/20" },
  declined:  { label: "Declined",  cls: "border-red-500/40 text-red-300 bg-red-500/10" },
  expired:   { label: "Expired",   cls: "border-orange-500/40 text-orange-300 bg-orange-500/10" },
  error:     { label: "Error",     cls: "border-red-500/60 text-red-200 bg-red-500/20" },
  draft:     { label: "Draft",     cls: "border-amber-400/40 text-amber-300 bg-amber-400/10" },
};

/* ---------------- Bookings ---------------- */
export default function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filter, setFilter] = useState({ status: "", type: "" });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [preview, setPreview] = useState(null); // { title, src }
  const [showManualModal, setShowManualModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [depositBooking, setDepositBooking] = useState(null);
  const [verifyBooking, setVerifyBooking] = useState(null);
  const [deliveryBooking, setDeliveryBooking] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const askDelete = async (b) => {
    const ok = await confirm({
      title: "Delete this booking?",
      message: "Removes it from the schedule immediately.",
      meta: `${b.full_name || b.customer?.name || "customer"} · ${b.id}`,
      note: "This action archives the booking to cancelled_bookings for audit and removes it from the live list. It cannot be undone from the UI.",
      confirmLabel: "Yes, delete booking",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/bookings/${b.id}`);
      toast.success("Booking deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const authQ = () => {
    const t = localStorage.getItem("pr_session_token");
    return t ? `?auth=${encodeURIComponent(t)}` : "";
  };
  const fileUrl = (fileId) => fileId
    ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/uploads/${fileId}${authQ()}`
    : null;

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.type) params.booking_type = filter.type;
    Promise.all([
      api.get("/admin/bookings", { params }),
      api.get("/admin/drivers"),
    ]).then(([b, d]) => { setBookings(b.data); setDrivers(d.data); })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(load, [load]);

  const assign = async (booking, driverId) => {
    if (!driverId) return;
    try {
      await api.post(`/admin/bookings/${booking.id}/assign-driver`, { driver_id: driverId });
      toast.success("Driver assigned");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approve = async (booking) => {
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/approve`, {
        origin_url: window.location.origin,
      });
      const msg = data?.approval_email_delivered
        ? "Approved & customer emailed"
        : "Approved (email not delivered — check Resend config)";
      toast.success(msg);
      if (data?.deposit_pay_url) {
        // Also expose the link so staff can copy it for SMS
        toast.info(`Deposit link: ${data.deposit_pay_url}`, { duration: 8000 });
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deny = async (booking) => {
    const reason = prompt("Optional note for the customer (why we can't confirm):", "");
    if (reason === null) return; // user cancelled
    try {
      await api.post(`/admin/bookings/${booking.id}/deny`, { reason });
      toast.success("Denied & customer emailed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (booking, status) => {
    try {
      await api.post(`/admin/bookings/${booking.id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };
  const setDeposit = async (booking, deposit_status) => {
    try {
      await api.post(`/admin/bookings/${booking.id}/deposit-status`, { deposit_status });
      toast.success(`Deposit ${deposit_status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const uploadInspection = async (booking, fileEvt) => {
    const file = fileEvt.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/admin/bookings/${booking.id}/inspection-video`, fd,
      );
      if (data.email_delivered) toast.success("Inspection video uploaded — contract emailed to customer");
      else toast.success("Inspection video uploaded. Email pending (add RESEND_API_KEY to auto-send).");
      // Give staff the copyable link either way
      if (data.share_url) navigator.clipboard?.writeText(data.share_url).catch(() => {});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Video upload failed"); }
    fileEvt.target.value = "";
  };
  const resendContract = async (booking) => {
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/resend-contract`);
      if (data.email_delivered) toast.success("Contract email resent to customer");
      else toast.info("Email pending (RESEND_API_KEY missing). Share link copied.");      if (data.share_url) navigator.clipboard?.writeText(data.share_url).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const copyShareLink = (booking) => {
    if (!booking.share_token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/booking/${booking.id}/inspection?token=${booking.share_token}`;
    navigator.clipboard?.writeText(url).then(() => toast.success("Share link copied")).catch(() => {});
  };

  const downloadContractPdf = async (booking) => {
    try {
      const res = await api.get(`/admin/bookings/${booking.id}/contract-pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plenty-rydez-contract-${booking.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Contract PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate contract PDF");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          data-testid="bo-filter-status"
          className="bg-ink-800 border border-white/10 rounded-full px-4 py-2 text-sm text-white">
          <option value="">All statuses</option>
          <option value="pending_approval">Pending approval</option>
          <option value="pending_payment">Pending payment</option>
          <option value="confirmed">Confirmed</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          data-testid="bo-filter-type"
          className="bg-ink-800 border border-white/10 rounded-full px-4 py-2 text-sm text-white">
          <option value="">Rides + rentals</option>
          <option value="ride">Rides only</option>
          <option value="rental">Rentals only</option>
        </select>
        <button
          type="button"
          data-testid="bo-add-manual-booking"
          onClick={() => setShowManualModal(true)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs label-caps bg-lime-400 text-ink-900 hover:brightness-110"
        >
          <Plus size={12} /> New booking
        </button>
      </div>

      {showManualModal && (
        <ManualBookingModal
          onClose={() => setShowManualModal(false)}
          onCreated={() => { setShowManualModal(false); load(); toast.success("Booking added to the schedule"); }}
        />
      )}
      {editingBooking && (
        <ManualBookingModal
          editing={editingBooking}
          onClose={() => setEditingBooking(null)}
          onCreated={() => { setEditingBooking(null); load(); toast.success("Booking updated"); }}
        />
      )}
      {depositBooking && (
        <LogDepositModal
          booking={depositBooking}
          onClose={() => setDepositBooking(null)}
          onSaved={() => { setDepositBooking(null); load(); toast.success("Deposit recorded — now Held in ledger"); }}
        />
      )}
      {verifyBooking && (
        <VerifyManualPaymentModal
          booking={verifyBooking}
          onClose={() => setVerifyBooking(null)}
          onSaved={() => { setVerifyBooking(null); load(); }}
        />
      )}

      {loading ? <Skeleton /> : bookings.length === 0 ? (
        <div className="rounded-3xl p-10 text-center text-white/60 bg-ink-900 border border-white/10">No bookings match those filters.</div>
      ) : (
        <div data-testid="bo-bookings-list" className="space-y-3">
          {bookings.map((b) => {
            const open = !!expanded[b.id];
            const depoPill = {
              pending: "bg-white/8 text-white/70",
              held: "bg-yellow-500/15 text-yellow-300 border border-yellow-500/40",
              refunded: "bg-lime-500/15 text-lime-300 border border-lime-500/40",
              forfeited: "bg-red-500/15 text-red-300 border border-red-500/40",
            }[b.deposit_status || "pending"];
            return (
              <div key={b.id} data-testid={`bo-booking-${b.id}`}
                className="rounded-2xl p-5 bg-ink-900 border border-white/10 shadow-[0_0_0_1px_rgba(0,0,0,0.4)]">
                <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`label-caps px-2 py-0.5 rounded-full border ${
                        b.type === "ride"
                          ? "bg-burgundy/10 border-burgundy/30 text-burgundy"
                          : "bg-platinum/10 border-platinum/30 text-platinum"
                      }`}>{b.type}</span>
                      {b.status === "pending_approval" ? (
                        <span className="label-caps px-2 py-0.5 rounded-full text-[10px] bg-amber-400/20 text-amber-200 border border-amber-400/50 animate-pulse">
                          Needs approval
                        </span>
                      ) : (
                        <span className="label-caps text-white/40">{b.status}</span>
                      )}
                      <span className="label-caps text-white/40">{b.payment_status}</span>
                      {(b.payment_status === "pending_manual_verification" || b.payment_status === "partial_paid") && (
                        <button
                          type="button"
                          data-testid={`bo-verify-payment-${b.id}`}
                          onClick={() => setVerifyBooking(b)}
                          className="label-caps px-2 py-0.5 rounded-full text-[10px] bg-cyan/15 border border-cyan/40 text-cyan hover:bg-cyan hover:text-midnight transition inline-flex items-center gap-1 animate-pulse">
                          <DollarSign size={9} /> Verify payment
                        </button>
                      )}
                      {b.payment_status === "paid" && (b.verified_paid_amount || (b.payment_verifications || []).length > 0) && (
                        <span className="label-caps px-2 py-0.5 rounded-full text-[10px] bg-lime-400/15 text-lime-300 border border-lime-400/40">
                          Verified ✓
                        </span>
                      )}
                      {b.type === "rental" && (
                        <span data-testid={`bo-booking-deposit-pill-${b.id}`}
                          className={`label-caps px-2 py-0.5 rounded-full text-[10px] ${depoPill}`}>
                          Deposit · {b.deposit_status || "pending"}
                        </span>
                      )}
                      {b.type === "rental" && (
                        <span data-testid={`bo-booking-inspection-pill-${b.id}`}
                          className={`label-caps px-2 py-0.5 rounded-full text-[10px] ${
                            b.inspection_video_file_id
                              ? "bg-lime-500/15 text-lime-300 border border-lime-500/40"
                              : "bg-orange-500/15 text-orange-300 border border-orange-500/40"
                          }`}>
                          {b.inspection_video_file_id ? "Inspection ✓" : "Inspection needed"}
                        </span>
                      )}
                      {b.payment_method === "cash" && (
                        <span className="label-caps px-2 py-0.5 rounded-full text-[10px] bg-gold/10 border border-gold/30 text-gold">CASH</span>
                      )}
                    </div>
                    <div className="font-serif text-lg">{b.vehicle_name}</div>
                    <div className="text-sm text-white/60 mt-1">
                      {b.full_name || b.customer?.name} · {b.customer_email || b.customer?.email}
                      {b.customer_phone ? ` · ${b.customer_phone}` : ""}
                    </div>
                    {b.invoice_number && (
                      <div className="text-xs text-amber-200/90 mt-1 inline-flex items-center gap-1"
                           data-testid={`bo-booking-invoice-link-${b.id}`}>
                        <span className="text-white/40">from invoice</span>
                        <a href="#invoices"
                           onClick={(e) => {
                             e.preventDefault();
                             window.dispatchEvent(new CustomEvent("bo:jump-to-invoice",
                               { detail: { invoice_id: b.invoice_id,
                                            invoice_number: b.invoice_number } }));
                           }}
                           className="font-mono px-1.5 py-0.5 rounded bg-amber-400/10 border border-amber-400/30 hover:bg-amber-400/20 transition">
                          {b.invoice_number}
                        </a>
                      </div>
                    )}
                    {b.type === "ride" && (
                      <div className="text-sm text-white/50 mt-1">
                        {b.pickup} → {b.dropoff} · {new Date(b.pickup_datetime).toLocaleString()}
                      </div>
                    )}
                    {b.type === "rental" && (
                      <div className="text-sm text-white/50 mt-1">
                        {new Date(b.start_at).toLocaleDateString()} — {new Date(b.end_at).toLocaleDateString()} · {b.days}d
                        {b.daily_mileage_limit ? ` · ${b.daily_mileage_limit} mi/day` : " · unlimited miles"}
                      </div>
                    )}
                    {b.driver_name && (
                      <div className="text-sm text-burgundy mt-1">Driver: {b.driver_name} ({b.driver_phone})</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 min-w-[220px]">
                    <div className="font-serif text-xl text-platinum">${b.amount?.toFixed(2)}</div>
                    {b.type === "ride" && b.deposit_amount != null && (
                      <div className="text-xs text-white/50">deposit ${Number(b.deposit_amount).toFixed(2)} ({Math.round((b.deposit_percent||0.5)*100)}%)</div>
                    )}
                    {b.type === "rental" && b.deposit != null && (
                      <div className="text-xs text-white/50">deposit ${Number(b.deposit).toFixed(2)}</div>
                    )}
                    {b.type === "ride" && (
                      <select
                        data-testid={`bo-assign-${b.id}`}
                        value={b.driver_id || ""}
                        onChange={(e) => assign(b, e.target.value)}
                        className="bg-ink-800 border border-white/10 rounded-full px-3 py-1.5 text-xs w-full text-white"
                      >
                        <option value="">Assign driver…</option>
                        {drivers.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    )}
                    <div className="flex gap-1 flex-wrap justify-end">
                      {b.status === "pending_approval" && (
                        <>
                          <button onClick={() => approve(b)}
                            data-testid={`bo-approve-${b.id}`}
                            className="px-3 py-1 rounded-full text-xs bg-lime-400 text-black font-semibold hover:bg-lime-300">
                            Approve
                          </button>
                          <button onClick={() => deny(b)}
                            data-testid={`bo-deny-${b.id}`}
                            className="px-3 py-1 rounded-full text-xs border border-red-500/50 text-red-300 hover:bg-red-500/10">
                            Deny
                          </button>
                        </>
                      )}
                      {b.status !== "in_progress" && b.status !== "completed" && b.status !== "pending_approval" && b.status !== "cancelled" && (
                        <button onClick={() => setStatus(b, "in_progress")}
                          className="px-2 py-1 rounded-full text-xs border border-white/10 hover:border-platinum">
                          In-progress
                        </button>
                      )}
                      {b.status !== "completed" && b.status !== "pending_approval" && b.status !== "cancelled" && (
                        <button onClick={() => setStatus(b, "completed")}
                          className="px-2 py-1 rounded-full text-xs border border-white/10 hover:border-burgundy">
                          Complete
                        </button>
                      )}
                      <button
                        data-testid={`bo-booking-edit-${b.id}`}
                        onClick={() => setEditingBooking(b)}
                        className="px-2 py-1 rounded-full text-xs border border-electric/40 text-electric hover:bg-electric hover:text-midnight inline-flex items-center gap-1">
                        <Pencil size={11} /> Edit
                      </button>
                      {!(b.deposit_paid_amount > 0) && (
                        <button
                          data-testid={`bo-booking-log-deposit-${b.id}`}
                          onClick={() => setDepositBooking(b)}
                          className="px-2 py-1 rounded-full text-xs border border-amber-400/40 text-amber-300 hover:bg-amber-400 hover:text-ink-900 inline-flex items-center gap-1">
                          <MoneyIcon size={11} /> Log deposit
                        </button>
                      )}
                      <button
                        data-testid={`bo-booking-delete-${b.id}`}
                        onClick={() => askDelete(b)}
                        className="px-2 py-1 rounded-full text-xs border border-red-500/40 text-red-300 hover:bg-red-500 hover:text-white inline-flex items-center gap-1">
                        <Trash2 size={11} /> Delete
                      </button>
                      {b.type === "rental" && (
                        <button
                          data-testid={`bo-booking-toggle-${b.id}`}
                          onClick={() => setExpanded((prev) => ({ ...prev, [b.id]: !prev[b.id] }))}
                          className="px-2 py-1 rounded-full text-xs border border-electric/40 text-electric hover:bg-electric/10">
                          {open ? "Hide details" : "Docs & deposit"}
                        </button>
                      )}
                      {b.type === "rental" && b.status !== "cancelled" && (() => {
                        const paid = Number(b.balance_paid_amount || 0);
                        const total = Number(b.amount || 0);
                        const remaining = Math.max(0, +(total - paid).toFixed(2));
                        if (remaining <= 0) return null;
                        return (
                          <button
                            data-testid={`bo-booking-collect-balance-${b.id}`}
                            onClick={async () => {
                              try {
                                const { data } = await api.post(`/admin/bookings/${b.id}/collect-balance`);
                                await navigator.clipboard.writeText(data.url).catch(() => {});
                                toast.success(`Balance link copied · $${data.remaining_balance.toFixed(2)} + $${data.processing_fee.toFixed(2)} fee`);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Couldn't create balance link");
                              }
                            }}
                            title={`Collect remaining $${remaining.toFixed(2)}`}
                            className="px-2 py-1 rounded-full text-xs border border-amber-400/50 text-amber-300 hover:bg-amber-400/10 inline-flex items-center gap-1">
                            <Link2 size={11} /> Collect ${remaining.toFixed(2)}
                          </button>
                        );
                      })()}
                      {(b.contract_signed_at || b.signature_data) && (
                        <button
                          data-testid={`bo-booking-download-pdf-${b.id}`}
                          onClick={() => downloadContractPdf(b)}
                          title="Download signed contract PDF"
                          className="px-2 py-1 rounded-full text-xs border border-lime-400/40 text-lime-300 hover:bg-lime-400/10 inline-flex items-center gap-1">
                          <FileDown size={11} /> PDF
                        </button>
                      )}
                      {b.type === "rental" && !b.pandadoc_document_id && (
                        <button
                          data-testid={`bo-booking-send-contract-${b.id}`}
                          onClick={async () => {
                            try {
                              await api.post(`/admin/pandadoc/bookings/${b.id}/send-contract`);
                              toast.success("Contract sent via PandaDoc");
                              load();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Send failed");
                            }
                          }}
                          title="Send rental agreement via PandaDoc"
                          className="px-2 py-1 rounded-full text-xs border border-cyan/40 text-cyan hover:bg-cyan/10 inline-flex items-center gap-1">
                          <Send size={11} /> Send Contract
                        </button>
                      )}
                      {b.pandadoc_document_id && b.pandadoc_status && (
                        <span
                          data-testid={`bo-booking-contract-status-${b.id}`}
                          className={`px-2 py-1 rounded-full text-[10px] label-caps border inline-flex items-center gap-1 ${
                            (CONTRACT_STATUS_STYLE[b.pandadoc_status] || CONTRACT_STATUS_STYLE.sent).cls
                          }`}>
                          <FileSignature size={10} /> {(CONTRACT_STATUS_STYLE[b.pandadoc_status] || CONTRACT_STATUS_STYLE.sent).label}
                        </span>
                      )}
                      {b.pandadoc_document_id && !["signed", "completed"].includes(b.pandadoc_status) && (
                        <button
                          data-testid={`bo-booking-resend-contract-${b.id}`}
                          onClick={async () => {
                            try {
                              await api.post(`/admin/pandadoc/bookings/${b.id}/resend`);
                              toast.success("Reminder sent");
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Resend failed");
                            }
                          }}
                          title="Resend rental agreement reminder"
                          className="px-2 py-1 rounded-full text-xs border border-white/15 text-white/70 hover:text-white hover:border-white/30 inline-flex items-center gap-1">
                          <RotateCcw size={11} /> Resend
                        </button>
                      )}
                      {b.type === "rental" && (
                        <button
                          data-testid={`bo-booking-final-delivery-${b.id}`}
                          onClick={() => setDeliveryBooking(b)}
                          title="Vehicle assignment & Final In-House Delivery Copy"
                          className="px-2 py-1 rounded-full text-xs border border-violet-400/40 text-violet-300 hover:bg-violet-400/10 inline-flex items-center gap-1">
                          <Truck size={11} /> Final Delivery
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {b.type === "rental" && open && (
                  <div data-testid={`bo-booking-details-${b.id}`}
                    className="mt-4 pt-4 border-t border-white/8 grid md:grid-cols-3 gap-4">
                    <DocTile
                      title="License · Front"
                      src={fileUrl(b.dl_front_file_id)}
                      onOpen={() => setPreview({ title: `${b.full_name} — DL Front`, src: fileUrl(b.dl_front_file_id) })}
                      testId={`bo-booking-dl-front-${b.id}`}
                    />
                    <DocTile
                      title="License · Back"
                      src={fileUrl(b.dl_back_file_id)}
                      onOpen={() => setPreview({ title: `${b.full_name} — DL Back`, src: fileUrl(b.dl_back_file_id) })}
                      testId={`bo-booking-dl-back-${b.id}`}
                    />
                    <div className="rounded-xl border border-white/10 p-3 bg-ink-950/50">
                      <div className="label-caps text-white/50 text-[10px] mb-2">Signature</div>
                      {b.signature_data ? (
                        <img data-testid={`bo-booking-signature-${b.id}`} alt="signature"
                          src={b.signature_data}
                          className="w-full h-24 object-contain bg-white/95 rounded" />
                      ) : (
                        <div className="text-xs text-white/40">Not signed</div>
                      )}
                    </div>
                    <InspectionVideoTile
                      booking={b}
                      onUpload={(e) => uploadInspection(b, e)}
                      onResend={() => resendContract(b)}
                      onCopyLink={() => copyShareLink(b)}
                    />
                    <div className="md:col-span-3 flex flex-wrap gap-3 items-center text-xs text-white/60">
                      {b.dl_number && <span>DL # <span className="text-white">{b.dl_number}</span></span>}
                      {b.contract_signed_at && <span>Signed <span className="text-white">{new Date(b.contract_signed_at).toLocaleString()}</span></span>}
                    </div>
                    <div className="md:col-span-3 flex flex-wrap gap-2 pt-2">
                      <button
                        data-testid={`bo-deposit-held-${b.id}`}
                        onClick={() => setDeposit(b, "held")}
                        className="px-3 py-1.5 rounded-full text-xs border border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/10">
                        Mark Held
                      </button>
                      <button
                        data-testid={`bo-deposit-refunded-${b.id}`}
                        onClick={() => setDeposit(b, "refunded")}
                        className="px-3 py-1.5 rounded-full text-xs border border-lime-500/50 text-lime-300 hover:bg-lime-500/10">
                        Mark Refunded
                      </button>
                      <button
                        data-testid={`bo-deposit-forfeited-${b.id}`}
                        onClick={() => setDeposit(b, "forfeited")}
                        className="px-3 py-1.5 rounded-full text-xs border border-red-500/50 text-red-300 hover:bg-red-500/10">
                        Forfeit
                      </button>
                      <button
                        data-testid={`bo-deposit-pending-${b.id}`}
                        onClick={() => setDeposit(b, "pending")}
                        className="px-3 py-1.5 rounded-full text-xs border border-white/15 text-white/60 hover:border-white/40">
                        Reset
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {preview && (
        <div
          data-testid="bo-doc-preview"
          onClick={() => setPreview(null)}
          className="fixed inset-0 bg-ink-950/90 z-50 flex items-center justify-center p-6 cursor-zoom-out">
          <div className="max-w-3xl w-full">
            <div className="label-caps text-white/60 text-xs mb-3 text-center">{preview.title} · click anywhere to close</div>
            <img src={preview.src} alt={preview.title} className="w-full max-h-[80vh] object-contain rounded-xl border border-white/10" />
          </div>
        </div>
      )}

      {confirmDialog}
    </div>
  );
}

function DocTile({ title, src, onOpen, testId }) {
  return (
    <div className="rounded-xl border border-white/10 p-3 bg-ink-950/50">
      <div className="label-caps text-white/50 text-[10px] mb-2">{title}</div>
      {src ? (
        <button type="button" onClick={onOpen} data-testid={testId}
          className="block w-full h-24 rounded overflow-hidden bg-ink-800 hover:ring-1 hover:ring-electric">
          <img src={src} alt={title} className="w-full h-full object-cover" />
        </button>
      ) : (
        <div className="text-xs text-white/40">Not uploaded</div>
      )}
    </div>
  );
}

function InspectionVideoTile({ booking, onUpload, onResend, onCopyLink }) {
  const inputRef = useRef(null);
  const has = !!booking.inspection_video_file_id;
  const shareUrl = booking.share_token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/booking/${booking.id}/inspection?token=${booking.share_token}`
    : null;
  return (
    <div className={`md:col-span-3 rounded-xl border p-4 ${
      has ? "border-lime-500/40 bg-lime-500/5" : "border-orange-500/40 bg-orange-500/5"
    }`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="label-caps text-[10px] text-white/60">
            {has ? "Inspection video · contract finalized" : "Pre-rental vehicle inspection"}
          </div>
          <div className="text-sm text-white/85 mt-0.5">
            {has
              ? <>Uploaded <span className="text-white">{new Date(booking.inspection_video_uploaded_at).toLocaleString()}</span> by <span className="text-white">{booking.inspection_video_uploaded_by}</span></>
              : "The contract is not sent to the customer until you upload a walk-around video of the vehicle's current condition."}
          </div>
          {has && (
            <div className="text-[11px] text-white/50 mt-1">
              Email: {booking.contract_email_delivered
                ? <span className="text-lime-300">delivered to {booking.customer_email}</span>
                : <span className="text-orange-300">pending — add RESEND_API_KEY to auto-send</span>}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
            className="hidden"
            data-testid={`bo-inspection-file-${booking.id}`}
            onChange={onUpload}
          />
          <button type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={`bo-inspection-upload-${booking.id}`}
            className={`px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5 ${
              has
                ? "border border-white/15 text-white/60 hover:border-white/40"
                : "bg-orange-500 text-ink-900 font-semibold hover:brightness-110"
            }`}>
            {has ? "Replace video" : "Upload Inspection Video"}
          </button>
          {has && (
            <>
              <button type="button"
                onClick={onResend}
                data-testid={`bo-inspection-resend-${booking.id}`}
                className="px-3 py-1.5 rounded-full text-xs border border-electric/40 text-electric hover:bg-electric/10">
                Resend contract email
              </button>
              <button type="button"
                onClick={onCopyLink}
                data-testid={`bo-inspection-copy-${booking.id}`}
                className="px-3 py-1.5 rounded-full text-xs border border-white/15 text-white/70 hover:border-white/40">
                Copy link
              </button>
            </>
          )}
        </div>
      </div>
      {has && shareUrl && (
        <div className="rounded overflow-hidden bg-ink-950 border border-white/10">
          <video
            data-testid={`bo-inspection-video-${booking.id}`}
            src={shareUrl}
            controls
            preload="metadata"
            className="w-full max-h-72 bg-black"
          />
        </div>
      )}
    </div>
  );
}



/* ---------------- Manual / Edit Booking Modal ---------------- */
function ManualBookingModal({ onClose, onCreated, editing = null }) {
  const isEdit = !!editing;
  const [vehicles, setVehicles] = useState([]);
  const _initDate = editing?.pickup_datetime || editing?.start_at || "";
  const [form, setForm] = useState({
    type: editing?.type || "ride",
    vehicle_id: editing?.vehicle_id || "",
    custom_vehicle_name: editing?.custom_vehicle_name || (editing && !editing.vehicle_id ? (editing.vehicle_name || "") : ""),
    full_name: editing?.full_name || "",
    phone: editing?.customer_phone || "",
    email: editing?.customer_email || "",
    pickup: editing?.pickup || "",
    dropoff: editing?.dropoff || "",
    pickup_date: _initDate ? _initDate.slice(0, 10) : "",
    pickup_time: _initDate ? _initDate.slice(11, 16) : "",
    passenger_count: editing?.passenger_count ?? 1,
    estimated_duration_hours: editing?.estimated_duration_hours ?? 3,
    trip_purpose: editing?.trip_purpose || "hourly",
    // rental fields
    start_at: editing?.start_at || "",
    end_at: editing?.end_at || "",
    pickup_location: editing?.pickup_location || "",
    return_location: editing?.return_location || "",
    // common
    special_requests: editing?.special_requests || "",
    payment_method: editing?.payment_method || "cash",
    amount: editing?.amount != null ? String(editing.amount) : "",
    deposit_paid_amount: editing?.deposit_paid_amount != null ? String(editing.deposit_paid_amount) : "",
    deposit_amount: editing?.deposit_amount != null ? String(editing.deposit_amount) : "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/vehicles").then((r) => {
      setVehicles(r.data || []);
      if (r.data?.length && !form.vehicle_id) {
        setForm((f) => ({ ...f, vehicle_id: r.data[0].id }));
      }
    });
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isCustomVehicle = form.vehicle_id === "__other__";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Pick a vehicle");
    if (isCustomVehicle && !(form.custom_vehicle_name || "").trim())
      return toast.error("Enter the vehicle name");
    if (isCustomVehicle && !form.amount)
      return toast.error("Enter a price for the custom vehicle");
    if (!form.full_name.trim()) return toast.error("Customer name is required");
    setBusy(true);
    try {
      const body = {
        vehicle_id: isCustomVehicle ? null : form.vehicle_id,
        custom_vehicle_name: isCustomVehicle ? form.custom_vehicle_name.trim() : null,
        type: form.type,
        full_name: form.full_name.trim(),
        phone: form.phone || null,
        email: form.email || null,
        special_requests: form.special_requests || null,
        payment_method: form.payment_method,
        amount: form.amount ? Number(form.amount) : null,
        deposit_paid_amount: form.deposit_paid_amount ? Number(form.deposit_paid_amount) : null,
        deposit_amount: form.deposit_amount ? Number(form.deposit_amount) : null,
      };
      if (form.type === "ride") {
        if (!form.pickup_date || !form.pickup_time) {
          setBusy(false);
          return toast.error("Pickup date & time are required");
        }
        body.pickup = form.pickup;
        body.dropoff = form.dropoff;
        body.pickup_datetime = new Date(`${form.pickup_date}T${form.pickup_time}`).toISOString();
        body.passenger_count = Number(form.passenger_count) || 1;
        body.estimated_duration_hours = Number(form.estimated_duration_hours) || 3;
        body.trip_purpose = form.trip_purpose;
        body.trip_type = "hourly";
      } else {
        if (!form.start_at || !form.end_at) {
          setBusy(false);
          return toast.error("Rental start & end dates are required");
        }
        body.start_at = new Date(form.start_at).toISOString();
        body.end_at = new Date(form.end_at).toISOString();
        body.pickup_location = form.pickup_location;
        body.return_location = form.return_location || form.pickup_location;
      }
      await api[isEdit ? "patch" : "post"](
        isEdit ? `/admin/bookings/${editing.id}` : "/admin/bookings/manual",
        body,
      );
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create booking");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="bo-manual-booking-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-start justify-center p-4 overflow-y-auto"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-2xl bg-ink-900 border border-lime-400/40 rounded-3xl p-6 my-8 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div className="label-caps text-lime-400 text-xs">{isEdit ? "Edit booking" : "Add booking to schedule"}</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div className="text-xs text-white/60 -mt-2">
          Manually enter a reservation (walk-in, phone call, etc). It goes straight to the schedule as <b className="text-lime-300">confirmed</b>.
        </div>

        {/* Type selector */}
        <div className="grid grid-cols-2 gap-2">
          <button type="button" data-testid="mb-type-ride"
            onClick={() => set("type", "ride")}
            className={`py-2 rounded-full text-xs label-caps border transition ${
              form.type === "ride" ? "bg-electric/20 border-electric text-electric" : "bg-white/5 border-white/10 text-white/60"
            }`}>Chauffeured ride</button>
          <button type="button" data-testid="mb-type-rental"
            onClick={() => set("type", "rental")}
            className={`py-2 rounded-full text-xs label-caps border transition ${
              form.type === "rental" ? "bg-lime-400/20 border-lime-400 text-lime-300" : "bg-white/5 border-white/10 text-white/60"
            }`}>Self-drive rental</button>
        </div>

        <F label="Vehicle">
          <select data-testid="mb-vehicle" value={form.vehicle_id}
            onChange={(e) => set("vehicle_id", e.target.value)} className="nv-input" required>
            <option value="">— Select vehicle —</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.name} · ${v.hourly_rate}/hr · ${v.daily_rate}/day</option>
            ))}
            <option value="__other__">Other — type name + price below</option>
          </select>
        </F>
        {isCustomVehicle && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Other vehicle name *">
              <input
                data-testid="mb-vehicle-other"
                value={form.custom_vehicle_name}
                onChange={(e) => set("custom_vehicle_name", e.target.value)}
                className="nv-input"
                placeholder="e.g. Partner's 2025 Cullinan" />
            </F>
            <F label="Price ($) *">
              <input
                type="number" min="0" step="0.01"
                data-testid="mb-vehicle-other-price"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                className="nv-input"
                placeholder="1200" />
            </F>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <F label="Customer name *">
            <input data-testid="mb-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
              className="nv-input" placeholder="Full name" required />
          </F>
          <F label="Phone">
            <input data-testid="mb-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
              className="nv-input" placeholder="+1 917…" />
          </F>
          <F label="Email (optional)">
            <input data-testid="mb-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
              className="nv-input" placeholder="you@example.com" />
          </F>
        </div>

        {form.type === "ride" ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <F label="Pickup"><input data-testid="mb-pickup" value={form.pickup} onChange={(e) => set("pickup", e.target.value)}
                className="nv-input" placeholder="e.g. JFK Terminal 4" /></F>
              <F label="Drop-off"><input data-testid="mb-dropoff" value={form.dropoff} onChange={(e) => set("dropoff", e.target.value)}
                className="nv-input" placeholder="e.g. Manhattan" /></F>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <F label="Date *"><input data-testid="mb-date" type="date" value={form.pickup_date}
                onChange={(e) => set("pickup_date", e.target.value)} className="nv-input" required /></F>
              <F label="Time *"><input data-testid="mb-time" type="time" value={form.pickup_time}
                onChange={(e) => set("pickup_time", e.target.value)} className="nv-input" required /></F>
              <F label="Passengers"><input data-testid="mb-pax" type="number" min={1} value={form.passenger_count}
                onChange={(e) => set("passenger_count", e.target.value)} className="nv-input" /></F>
              <F label="Hours"><input data-testid="mb-hours" type="number" min={1} step={0.5} value={form.estimated_duration_hours}
                onChange={(e) => set("estimated_duration_hours", e.target.value)} className="nv-input" /></F>
            </div>
            <F label="Trip purpose">
              <select data-testid="mb-purpose" value={form.trip_purpose}
                onChange={(e) => set("trip_purpose", e.target.value)} className="nv-input">
                <option value="hourly">Hourly / point-to-point</option>
                <option value="airport">Airport transfer</option>
                <option value="corporate">Corporate / executive</option>
                <option value="photoshoot">Photoshoot / Production</option>
                <option value="wedding">Wedding</option>
                <option value="prom">Prom</option>
                <option value="concert">Concert</option>
                <option value="sporting_event">Sporting event</option>
                <option value="nightlife">Nightlife</option>
                <option value="funeral">Funeral</option>
                <option value="medical">Medical transportation</option>
                <option value="cruise">Cruise terminal</option>
                <option value="other">Other</option>
              </select>
            </F>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <F label="Start *"><input data-testid="mb-start" type="datetime-local" value={form.start_at}
                onChange={(e) => set("start_at", e.target.value)} className="nv-input" required /></F>
              <F label="End *"><input data-testid="mb-end" type="datetime-local" value={form.end_at}
                onChange={(e) => set("end_at", e.target.value)} className="nv-input" required /></F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Pickup location"><input data-testid="mb-pu-loc" value={form.pickup_location}
                onChange={(e) => set("pickup_location", e.target.value)} className="nv-input" /></F>
              <F label="Return location"><input data-testid="mb-ret-loc" value={form.return_location}
                onChange={(e) => set("return_location", e.target.value)} className="nv-input" /></F>
            </div>
          </>
        )}

        <F label="Notes / special requests">
          <textarea data-testid="mb-notes" value={form.special_requests}
            onChange={(e) => set("special_requests", e.target.value)} rows={2}
            className="nv-input" placeholder="Water preferences, car seat, meet & greet, VIP handling…" />
        </F>

        <div className="grid grid-cols-2 gap-3">
          <F label="Payment method">
            <select data-testid="mb-payment" value={form.payment_method}
              onChange={(e) => set("payment_method", e.target.value)} className="nv-input">
              <option value="cash">Cash · at pickup</option>
              <option value="card">Card · manual charge</option>
              <option value="invoice">Invoice</option>
              <option value="manual">Other / manual</option>
            </select>
          </F>
          <F label="Amount (override, optional)">
            <input data-testid="mb-amount" type="number" min={0} step={0.01} value={form.amount}
              onChange={(e) => set("amount", e.target.value)} className="nv-input" placeholder="Auto-calc if blank" />
          </F>
        </div>

        <div className="rounded-2xl border border-amber-400/40 bg-amber-400/5 p-3">
          <div className="label-caps text-amber-300 text-[10px] mb-2">Deposit collected (optional)</div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Deposit paid (held)">
              <input data-testid="mb-deposit-paid" type="number" min={0} step={0.01}
                value={form.deposit_paid_amount}
                onChange={(e) => set("deposit_paid_amount", e.target.value)}
                className="nv-input" placeholder="e.g. 200.00" />
            </F>
            <F label="Deposit target (optional)">
              <input data-testid="mb-deposit-target" type="number" min={0} step={0.01}
                value={form.deposit_amount}
                onChange={(e) => set("deposit_amount", e.target.value)}
                className="nv-input" placeholder="Full deposit expected" />
            </F>
          </div>
          <div className="text-[10px] text-white/50 mt-2">
            If you enter any amount here, it appears in the Deposits ledger as <b className="text-amber-300">Held</b> — you can later Release, Refund, or Deduct for damages from that tab.
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 rounded-full py-2.5 text-xs label-caps border border-white/15 text-white/70 hover:text-white">
            Cancel
          </button>
          <button type="submit" data-testid="mb-submit" disabled={busy}
            className="flex-[2] rounded-full py-2.5 text-xs label-caps bg-lime-400 text-ink-900 hover:brightness-110 disabled:opacity-50">
            {busy ? (isEdit ? "Saving…" : "Adding…") : (isEdit ? "Save changes" : "Add to schedule")}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Quick Log Deposit Modal ---------------- */
function LogDepositModal({ booking, onClose, onSaved }) {
  const [amount, setAmount] = useState(
    booking.deposit_amount ? String(booking.deposit_amount) : ""
  );
  const [method, setMethod] = useState(booking.payment_method || "cash");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a deposit amount greater than $0");
    setBusy(true);
    try {
      await api.patch(`/admin/bookings/${booking.id}`, {
        deposit_paid_amount: amt,
        deposit_status: "held",
        payment_method: method,
      });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log deposit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bo-log-deposit-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-start justify-center p-4 overflow-y-auto">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md bg-ink-900 border border-amber-400/40 rounded-3xl p-6 my-8 space-y-4">
        <div className="flex items-center justify-between">
          <div className="label-caps text-amber-300 text-xs">Log deposit collected</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div className="bg-ink-950/60 rounded-xl p-3 text-xs space-y-0.5">
          <div className="text-white font-medium">{booking.full_name || "Customer"}</div>
          <div className="text-white/50">{booking.vehicle_name}</div>
          <div className="text-white/40 font-mono text-[10px]">{booking.id}</div>
        </div>
        <F label="Deposit amount collected *">
          <input data-testid="log-dep-amount" type="number" min={0.01} step={0.01}
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="nv-input" placeholder="e.g. 200.00" required autoFocus />
        </F>
        <F label="Payment method">
          <select data-testid="log-dep-method" value={method}
            onChange={(e) => setMethod(e.target.value)} className="nv-input">
            <option value="cash">Cash</option>
            <option value="card">Card · manual</option>
            <option value="zelle">Zelle</option>
            <option value="venmo">Venmo</option>
            <option value="paypal">PayPal</option>
            <option value="apple_pay">Apple Pay</option>
            <option value="other">Other</option>
          </select>
        </F>
        <div className="text-[11px] text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-xl px-3 py-2">
          This deposit will appear in the Deposits ledger as <b>Held</b> — later you can Release it to revenue, Refund it, or Deduct for damages.
        </div>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose}
            className="flex-1 rounded-full py-2.5 text-xs label-caps border border-white/15 text-white/70 hover:text-white">
            Cancel
          </button>
          <button type="submit" data-testid="log-dep-submit" disabled={busy}
            className="flex-[2] rounded-full py-2.5 text-xs label-caps bg-amber-400 text-ink-900 hover:brightness-110 disabled:opacity-50">
            {busy ? "Recording…" : `Log $${Number(amount || 0).toFixed(2)} as held`}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Verify Manual Payment Modal ---------------- */
function VerifyManualPaymentModal({ booking, onClose, onSaved }) {
  const owed = Number(booking.amount || 0);
  const alreadyVerified = Number(booking.verified_paid_amount || 0);
  const remaining = Math.max(0, +(owed - alreadyVerified).toFixed(2));
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState(remaining ? String(remaining) : String(owed));
  const [channel, setChannel] = useState(booking.payment_method || "cashapp_manual");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const history = booking.payment_verifications || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!reference.trim()) return toast.error("Enter the receipt reference / transaction ID");
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Verified amount must be > $0");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/verify-manual-payment`, {
        reference: reference.trim(),
        verified_amount: amt,
        channel,
        notes: notes.trim(),
      });
      toast.success(
        data.payment_status === "paid"
          ? `Verified in full — booking marked ${data.payment_status}`
          : `Recorded — $${data.verified_paid_amount.toFixed(2)} of $${owed.toFixed(2)} verified`
      );
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const CHANNELS = [
    { k: "cashapp_manual", l: "Cash App · $plentyr718" },
    { k: "apple_pay_manual", l: "Apple Pay · 718-840-9084" },
    { k: "zelle", l: "Zelle · 718-840-9084" },
    { k: "cash", l: "Cash · in-person" },
    { k: "other", l: "Other" },
  ];

  return (
    <div data-testid="bo-verify-payment-modal"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink-950/85 flex items-start justify-center p-4 overflow-y-auto">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md bg-ink-900 border border-cyan/40 rounded-3xl p-6 my-8 space-y-4">
        <div className="flex items-center justify-between">
          <div className="label-caps text-cyan text-xs">Verify manual payment</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div className="bg-ink-950/60 rounded-xl p-3 text-xs space-y-0.5">
          <div className="text-white font-medium">{booking.full_name || "Customer"}</div>
          <div className="text-white/50">{booking.vehicle_name}</div>
          <div className="text-white/40 font-mono text-[10px]">{booking.id}</div>
          <div className="flex justify-between mt-2 pt-2 border-t border-white/5">
            <span className="text-white/60">Amount owed</span>
            <span className="text-white font-mono">${owed.toFixed(2)}</span>
          </div>
          {alreadyVerified > 0 && (
            <div className="flex justify-between">
              <span className="text-white/60">Already verified</span>
              <span className="text-lime-300 font-mono">${alreadyVerified.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-white/60">Remaining</span>
            <span className="text-cyan font-mono">${remaining.toFixed(2)}</span>
          </div>
        </div>
        <F label="Payment channel">
          <select data-testid="verify-channel" value={channel}
            onChange={(e) => setChannel(e.target.value)} className="nv-input">
            {CHANNELS.map((c) => <option key={c.k} value={c.k}>{c.l}</option>)}
          </select>
        </F>
        <F label="Receipt reference / transaction ID *">
          <input data-testid="verify-reference" value={reference} autoFocus
            onChange={(e) => setReference(e.target.value)}
            className="nv-input" placeholder="Cash App tx ID · Zelle confirmation · etc." required />
        </F>
        <F label="Verified amount *">
          <input data-testid="verify-amount" type="number" min={0.01} step={0.01}
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="nv-input" required />
        </F>
        <F label="Notes (optional)">
          <textarea data-testid="verify-notes" rows={2}
            value={notes} onChange={(e) => setNotes(e.target.value)}
            className="nv-input" placeholder="Screenshot filename, comment on the transfer…" />
        </F>
        {history.length > 0 && (
          <div className="rounded-xl border border-white/8 p-3">
            <div className="label-caps text-[10px] text-white/50 mb-2">Prior verifications</div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {history.map((v) => (
                <div key={v.id} className="text-[11px] flex items-center gap-2 text-white/80">
                  <span className="text-lime-300 font-mono min-w-[60px]">${Number(v.amount).toFixed(2)}</span>
                  <span className="text-white/40 label-caps text-[9px] min-w-[80px]">{(v.channel || "").replace(/_/g,' ')}</span>
                  <span className="flex-1 truncate italic text-white/60">{v.reference}</span>
                  <span className="text-white/30 text-[9px]">{new Date(v.at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="text-[11px] text-cyan bg-cyan/10 border border-cyan/30 rounded-xl px-3 py-2">
          On save, we log the reference + amount + you as the verifier, and flip the booking to <b>paid</b> (or <b>partial_paid</b> if less than owed).
        </div>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose}
            className="flex-1 rounded-full py-2.5 text-xs label-caps border border-white/15 text-white/70">
            Cancel
          </button>
          <button type="submit" data-testid="verify-submit" disabled={busy}
            className="flex-[2] rounded-full py-2.5 text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 disabled:opacity-50">
            {busy ? "Recording…" : `Verify $${Number(amount || 0).toFixed(2)}`}
          </button>
        </div>
      </form>
    </div>
  );
}

